// Push while desyr is closed (28-push.md, "Keeping a device subscribed", "Signing out"): the browser's
// push subscription with desyr's key, this browser's pusher at the chat server (IDs only, with the
// device's quiet windows), and what the worker needs in Cache Storage. Mail pushes were a candidate
// to drop.
import { ClientEvent, type MatrixClient } from 'matrix-js-sdk';
import { useSyncExternalStore } from 'react';
import { config, entryFor } from '../core/config';
import { serverOf } from '../core/session';
import { onSettings, setting } from '../core/settings';
import { readJson } from '../core/storage';
import { currentLanguage, onLanguage } from '../i18n';
import { quietWindows, type QuietHours } from './quiet';
import { waiting } from '../accounts/store';

export const APP_ID = 'chat.desyr.web';
export const PUSH_CACHE = 'desyr_push';
const DAY = 86_400_000;

/** on, off, blocked by the browser, the last set-up failed; hidden: no gateway or no push here. */
export type PushState = 'on' | 'off' | 'blocked' | 'failed' | 'hidden';
let state: PushState = 'hidden';
const listeners = new Set<() => void>();
const setState = (s: PushState) => ((state = s), listeners.forEach((l) => l()));
export const usePushState = () => useSyncExternalStore((l) => (listeners.add(l), () => listeners.delete(l)), () => state);

export const canPush = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
export const pushSwitchedOff = () => !!readJson('desyr_push_off');
/** The gateway's notify address for this server (config.json `desyrPush`, the server's entry or "*"). */
export const notifyAddress = (client: MatrixClient) => entryFor(config().desyrPush, client.baseUrl, serverOf(client.getUserId()!)) ?? config().desyrPush?.['*'];

const browser = () => {
  const ua = navigator.userAgent;
  return /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'a browser';
};
const fromBase64url = (s: string) => Uint8Array.from(atob(s.replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));
const sameKey = (a: ArrayBuffer | null | undefined, b: Uint8Array) => !!a && a.byteLength === b.length && new Uint8Array(a).every((x, i) => x === b[i]);

/** The quiet windows by the person's quiet hours and Do Not Disturb (34). */
export const windowsOf = (client: MatrixClient) =>
  quietWindows(client.getAccountData('chat.desyr.quiet_hours' as never)?.getContent<QuietHours>(), client.getAccountData('chat.desyr.status' as never)?.getContent().status === 'dnd');

/**
 * This browser's subscription, asked for only once push is allowed: there can be none before, and
 * WebKit can hang the whole page on `getSubscription()` while push isn't allowed.
 */
async function subscriptionOf(registration: ServiceWorkerRegistration | undefined): Promise<PushSubscription | null> {
  return registration && Notification.permission === 'granted' ? registration.pushManager.getSubscription() : null;
}

/** The subscription when push is on here (and the browser allows it), made with desyr's key. */
export async function subscription(): Promise<PushSubscription | undefined> {
  if (!canPush() || Notification.permission !== 'granted' || !setting('desktop-notifications') || pushSwitchedOff()) return undefined;
  return (await subscriptionOf(await navigator.serviceWorker.ready)) ?? undefined;
}

let running: Promise<void> | undefined;

/** Registers this browser for push, or switches its pusher off; at most one at a time. */
export function keepSubscribed(client: MatrixClient): Promise<void> {
  running = (running ?? Promise.resolve()).then(() => subscribe(client)).catch(() => setState('failed'));
  return running;
}

async function subscribe(client: MatrixClient): Promise<void> {
  const notify = notifyAddress(client);
  if (!notify || !canPush()) return setState('hidden');
  const registration = await navigator.serviceWorker.ready;
  const existing = await subscriptionOf(registration);
  const permitted = Notification.permission === 'granted';
  if (!permitted || !setting('desktop-notifications') || pushSwitchedOff()) {
    const key = existing?.toJSON().keys?.p256dh;
    if (key) await client.setPusher({ kind: null, app_id: APP_ID, pushkey: key } as never).catch(() => {});
    return setState(Notification.permission === 'denied' ? 'blocked' : 'off');
  }
  const answer = await fetch('/_desyr/push/vapid', { credentials: 'omit', cache: 'no-store' });
  if (!answer.ok) throw new Error(`vapid ${answer.status}`);
  const key = fromBase64url((await answer.json()).publicKey);
  let sub = existing;
  if (sub && !sameKey(sub.options.applicationServerKey, key)) (await sub.unsubscribe(), (sub = null));
  sub ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key as BufferSource });
  const json = sub.toJSON();
  const quiet = windowsOf(client);
  await client.setPusher({
    kind: 'http',
    app_id: APP_ID,
    pushkey: json.keys!.p256dh,
    app_display_name: 'desyr',
    device_display_name: `desyr in ${browser()}`,
    lang: currentLanguage(),
    append: false,
    data: { url: notify, format: 'event_id_only', endpoint: json.endpoint, auth: json.keys!.auth, quiet },
  } as never);
  // What the worker needs while desyr is closed: the session and the quiet windows.
  const cache = await caches.open(PUSH_CACHE);
  // The waiting accounts' sessions too: their pushers stay while they wait (33).
  const sessions = [{ user_id: client.getUserId()!, base_url: client.baseUrl, access_token: client.getAccessToken()! }, ...waiting().filter((a) => !a.signed_out)];
  await cache.put('/sessions', new Response(JSON.stringify(sessions.map((a) => ({ userId: a.user_id, baseUrl: a.base_url, accessToken: a.access_token })))));
  await cache.put('/quiet', new Response(JSON.stringify(quiet)));
  await cache.put('/language', new Response(JSON.stringify(currentLanguage())));
  setState('on');
}

/** Keeps the device subscribed: now, daily, and when what it depends on changes. */
export function startPush(client: MatrixClient): () => void {
  const again = () => void keepSubscribed(client);
  again();
  const daily = setInterval(again, DAY);
  const offSettings = onSettings(again);
  const offLanguage = onLanguage(again);
  const onData = (ev: { getType(): string }) => ['chat.desyr.quiet_hours', 'chat.desyr.status'].includes(ev.getType()) && again();
  client.on(ClientEvent.AccountData, onData);
  // The browser's permission can change without telling (e.g. after Safari's dialog).
  addEventListener('focus', again);
  const onMessage = (e: MessageEvent) => e.data?.type === 'desyr:push-renewed' && again();
  navigator.serviceWorker?.addEventListener('message', onMessage);
  return () => {
    clearInterval(daily);
    offSettings();
    offLanguage();
    client.off(ClientEvent.AccountData, onData);
    removeEventListener('focus', again);
    navigator.serviceWorker?.removeEventListener('message', onMessage);
  };
}

/** Signing out the last account: nothing is pushed here any more (the chat server drops its pusher with the session). */
export async function stopPush(): Promise<void> {
  if (!canPush()) return;
  await caches.delete(PUSH_CACHE).catch(() => {});
  const sub = await subscriptionOf(await navigator.serviceWorker.getRegistration());
  await sub?.unsubscribe().catch(() => {});
}
